import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { FaArrowLeft, FaEye, FaFileAlt, FaCalendarAlt, FaUser, FaChevronDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchStudentReports, clearError, clearSuccessMessage } from '../../redux/slices/studentReportsSlice';
import { getParentChildren } from '../../redux/slices/lessonsSlice';
import styles from './StudentReports.module.css';

function StudentReports() {
  const { isDarkMode } = useDarkMode();
  const { studentId, classId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { reports, loading, error, successMessage } = useSelector((state) => state.studentReports);
  const { parentChildren, parentChildrenLoading } = useSelector((state) => state.lessons);
  const { currentUser } = useSelector((state) => state.auth);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChild, setSelectedChild] = useState(null);
  const [showChildrenDropdown, setShowChildrenDropdown] = useState(false);

  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isParent = userRole === "parent";

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isParent) {
      // لولي الأمر: جلب الأبناء
      dispatch(getParentChildren());
    } else if (studentId && classId) {
      // للمعلم: جلب تقارير طالب معين
      dispatch(fetchStudentReports({ studentId, classId }));
    }
  }, [dispatch, studentId, classId, isParent]);

  useEffect(() => {
    if (parentChildren && parentChildren.length > 0 && !selectedChild) {
      // تعيين الابن الأول كافتراضي
      setSelectedChild(parentChildren[0]);
      localStorage.setItem('selectedChildId', parentChildren[0].id);
    }
  }, [parentChildren, selectedChild]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [error, successMessage, dispatch]);



  const handleViewReport = (reportId) => {
    navigate(`/report-details/${reportId}`);
  };

  const handleBack = () => {
    if (isParent) {
      navigate('/');
    } else {
      navigate('/students');
    }
  };

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    setShowChildrenDropdown(false);
    localStorage.setItem('selectedChildId', child.id);
    // إعادة جلب التقارير للابن المحدد
    if (isParent) {
      dispatch(fetchStudentReports({ studentId: child.id, classId: 0 }));
    }
  };

  // Filter reports by search term only
  const filteredReports = reports.filter(report => {
    return !searchTerm || 
           (report.report_name && report.report_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (report.teacher_notes && report.teacher_notes.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'تاريخ غير صحيح';
    }
  };

  // Get report type badges
  const getReportTypeBadges = (report) => {
    const badges = [];
    
    if (report.absence) {
      badges.push({ text: 'غياب', type: 'absence' });
    }
    
    if (report.homework) {
      badges.push({ text: 'واجب', type: 'homework' });
    }
    
    if (report.weeknesses) {
      badges.push({ text: 'نقاط ضعف', type: 'weaknesses' });
    }
    
    // If no specific type, show general report
    if (badges.length === 0) {
      badges.push({ text: 'تقرير عام', type: 'general' });
    }
    
    return badges;
  };

  if (loading || (isParent && parentChildrenLoading)) {
    return (
      <div className={`${styles.studentReportsPage} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>جاري تحميل التقارير...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.studentReportsPage} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <FaArrowLeft />
            {isParent ? 'رجوع للرئيسية' : 'رجوع للطلاب'}
          </button>
          
          <div className={styles.titleSection}>
            <h1>{isParent ? 'تقارير الأبناء' : 'تقارير الطالب'}</h1>
            <p>{isParent ? 'عرض وإدارة تقارير الأبناء' : 'عرض وإدارة تقارير الطالب'}</p>
          </div>


        </div>

        {/* Children Dropdown for Parents */}
        {isParent && parentChildren && parentChildren.length > 0 && (
          <div className={styles.childrenSelector}>
            <div className={styles.dropdownContainer}>
              <button 
                className={styles.dropdownButton}
                onClick={() => setShowChildrenDropdown(!showChildrenDropdown)}
              >
                <span>{selectedChild ? selectedChild.name : 'اختر الابن'}</span>
                <FaChevronDown className={`${styles.dropdownIcon} ${showChildrenDropdown ? styles.rotated : ''}`} />
              </button>
              
              {showChildrenDropdown && (
                <div className={styles.dropdownMenu}>
                  {parentChildren.map((child) => (
                    <button
                      key={child.id}
                      className={`${styles.dropdownItem} ${selectedChild?.id === child.id ? styles.active : ''}`}
                      onClick={() => handleChildSelect(child)}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search Only */}
        <div className={styles.controlsSection}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="البحث في التقارير..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Reports Grid */}
        <div className={styles.reportsSection}>
          {filteredReports.length > 0 ? (
            <div className={styles.reportsGrid}>
              {filteredReports.map((report) => {
                const badges = getReportTypeBadges(report);
                
                return (
                  <div key={report.id} className={styles.reportCard}>
                    <div className={styles.reportHeader}>
                      <div className={styles.reportIcon}>
                        <FaFileAlt />
                      </div>
                      <div className={styles.reportNumber}>
                        تقرير #{report.id}
                      </div>
                    </div>

                    <div className={styles.reportContent}>
                      <h3 className={styles.reportTitle}>
                        {report.report_name || 'تقرير بدون عنوان'}
                      </h3>
                      
                      <div className={styles.reportDetails}>
                        <div className={styles.detailItem}>
                          <FaCalendarAlt />
                          <span>{formatDate(report.created_at)}</span>
                        </div>
                        
                        {/* Display report type badges */}
                        {badges.map((badge, index) => (
                          <div key={index} className={styles.detailItem}>
                            <span className={`${styles.badge} ${styles[badge.type]}`}>
                              {badge.text}
                            </span>
                          </div>
                        ))}
                      </div>

                      {report.test_degree && (
                        <div className={styles.testScore}>
                          <span>درجة الاختبار: {report.test_degree}</span>
                        </div>
                      )}

                      {report.teacher_notes && (
                        <p className={styles.teacherNotes}>
                          {report.teacher_notes.length > 100 
                            ? `${report.teacher_notes.substring(0, 100)}...` 
                            : report.teacher_notes
                          }
                        </p>
                      )}
                    </div>

                    <div className={styles.reportFooter}>
                      <button
                        className={styles.viewButton}
                        onClick={() => handleViewReport(report.id)}
                      >
                        <FaEye />
                        عرض التفاصيل
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <FaFileAlt className={styles.emptyIcon} />
              <h3>لا توجد تقارير</h3>
              <p>
                {searchTerm 
                  ? 'لا توجد تقارير تطابق البحث المحدد'
                  : 'لم يتم إنشاء أي تقارير لهذا الطالب بعد'
                }
              </p>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentReports;
